import type { ChangeEvent } from 'react';

import * as Sentry from '@sentry/react';
import type { FormikHelpers } from 'formik';
import noop from 'lodash/noop';
import { NavLink } from 'react-router-dom';

import { contact } from 'src/app/router/paths';
import type { InsuranceCard } from 'src/app/types';
import { reduceImageSize } from 'src/app/utils/reduceImageSize';
import {
  ID_UPLOAD_STATUS,
  IMAGE_UPLOAD_QUALITY,
  IMAGE_UPLOAD_SIZE,
  IMAGE_UPLOAD_WIDTH,
  INSURANCE_STRINGS,
} from 'src/consts';

export interface AddInsuranceStepValues {
  _generic?: any;
  frontCard: InsuranceCard;
  backCard: InsuranceCard;
  addInsurance: 'Yes' | 'No';
}

/* Insurance Card Upload Functions */
export const onUploadStartInsuranceCard = (
  cardType: string,
  values: AddInsuranceStepValues,
  setFieldValue: FormikHelpers<AddInsuranceStepValues>['setFieldValue']
) => {
  if (cardType === INSURANCE_STRINGS.frontCard) {
    setFieldValue('frontCard', { ...values.frontCard, status: ID_UPLOAD_STATUS.loading });
  } else if (cardType === INSURANCE_STRINGS.backCard) {
    setFieldValue('backCard', { ...values.backCard, status: ID_UPLOAD_STATUS.loading });
  }
};

export const onUploadInsuranceCard = (
  event: ChangeEvent<HTMLInputElement>,
  reduceImageSizeCallback: (blob: Blob | null) => void
) => {
  const file = event.target.files?.[0] ?? null;
  if (!file) throw new Error('No file');
  reduceImageSize(file, IMAGE_UPLOAD_SIZE, IMAGE_UPLOAD_WIDTH, Infinity, IMAGE_UPLOAD_QUALITY, reduceImageSizeCallback);
};

export const onUploadCompleteInsuranceCard = noop;

export const onUploadErrorInsuranceCard = (
  cardType: string,
  values: AddInsuranceStepValues,
  setFieldValue: FormikHelpers<AddInsuranceStepValues>['setFieldValue'],
  setErrors: FormikHelpers<AddInsuranceStepValues>['setErrors'],
  err: Error
) => {
  if (cardType === INSURANCE_STRINGS.frontCard) {
    setFieldValue('frontCard', {
      ...values.frontCard,
      status: ID_UPLOAD_STATUS.empty,
    });
  } else if (cardType === INSURANCE_STRINGS.backCard) {
    setFieldValue('backCard', {
      ...values.backCard,
      status: ID_UPLOAD_STATUS.empty,
    });
  }
  Sentry.captureException(err);
  setErrors({
    // @ts-ignore
    _generic: (
      <>
        There was an issue with your upload. Please try again, or <NavLink to={contact.toPath()}>contact us</NavLink>.
      </>
    ),
  });
};

export const reduceImageSizeCallback = (
  cardType: string,
  values: AddInsuranceStepValues,
  setFieldValue: FormikHelpers<AddInsuranceStepValues>['setFieldValue'],
  blob: Blob | null
) => {
  const url = blob ? URL.createObjectURL(blob) : '';

  if (cardType === INSURANCE_STRINGS.frontCard) {
    setFieldValue('frontCard', {
      ...values.frontCard,
      url,
      blob,
      status: ID_UPLOAD_STATUS.unverified,
    });

    if (values.backCard.status === ID_UPLOAD_STATUS.start) {
      setFieldValue('backCard', {
        ...values.backCard,
        url,
        blob,
        status: ID_UPLOAD_STATUS.empty,
      });
    }
  } else if (cardType === INSURANCE_STRINGS.backCard) {
    setFieldValue('backCard', {
      ...values.backCard,
      url,
      blob,
      status: ID_UPLOAD_STATUS.unverified,
    });
  }
};
